import { Reducer } from 'react';
import update from 'immutability-helper';

import { Content } from '@/services/contents/content.vm';
import { Route } from '@/services/routes/route.vm';
import { Stage } from '@/services/stages/stage.vm';
import { ContentsAction, ContentsActionTypes } from './actions';

export type ContentsState = Record<
  Route['id'],
  {
    contents: Content[];
    stages: Record<Stage['id'], Content[]>;
    contentsHasLoaded?: boolean;
    stagesHasLoaded?: Record<Stage['id'], boolean>;
  }
>;

export const contentsInitialState: ContentsState = {};

const orderContents = (contents: Content[] | undefined): Content[] => {
  if (!contents) return [];
  return contents.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

export const contentsDataReducer: Reducer<ContentsState, ContentsAction> = (state, action) => {
  switch (action.type) {
    case ContentsActionTypes.LOAD_LEARNING_PATH_CONTENTS: {
      const { routeId, contents } = action.payload;
      const routeContents = state[routeId];
      if (!routeContents)
        return {
          ...state,
          [routeId]: {
            contents: orderContents(contents),
            stages: {},
            contentsHasLoaded: true,
            stagesHasLoaded: {},
          },
        };
      return update(state, {
        [routeId]: {
          contents: { $set: orderContents(contents) },
        },
      });
    }

    case ContentsActionTypes.LOAD_LEARNING_CONTENT: {
      const { routeId, contentId, content } = action.payload;
      const routeContents = state[routeId];
      if (!routeContents)
        return {
          ...state,
          [routeId]: {
            contents: [content],
            stages: {},
          },
        };

      const newContents = routeContents.contents.map((prevContent) =>
        prevContent.id === contentId ? { ...prevContent, ...content } : prevContent,
      );

      return update(state, {
        [routeId]: {
          contents: { $set: orderContents(newContents) },
        },
      });
    }

    case ContentsActionTypes.ADD_CONTENT: {
      const { routeId, content } = action.payload;
      return update(state, {
        [routeId]: {
          contents: { $push: [content] },
        },
      });
    }

    case ContentsActionTypes.UPDATE_CONTENT: {
      const { routeId, contentId, content } = action.payload;
      const routeContents = state[routeId];
      if (!routeContents) return state;

      const newContents = routeContents.contents.map((prevContent) =>
        prevContent.id === contentId ? { ...prevContent, ...content } : prevContent,
      );

      return update(state, {
        [routeId]: {
          contents: { $set: orderContents(newContents) },
        },
      });
    }

    case ContentsActionTypes.DELETE_CONTENT: {
      const { routeId, contentId } = action.payload;
      const routeContents = state[routeId];
      if (!routeContents) return state;

      const newContents = routeContents.contents.filter((content) => content.id !== contentId);

      return update(state, {
        [routeId]: {
          contents: { $set: orderContents(newContents) },
        },
      });
    }

    case ContentsActionTypes.LOAD_STAGE_CONTENTS: {
      const { routeId, stageId, contents } = action.payload;
      const routeContents = state[routeId]?.stages?.[stageId];
      if (!routeContents) {
        return {
          ...state,
          [routeId]: {
            ...state[routeId],
            stages: {
              ...state[routeId].stages,
              [stageId]: orderContents(contents),
            },
            stagesHasLoaded: {
              ...state[routeId].stagesHasLoaded,
              [stageId]: true,
            },
          },
        };
      }
      return update(state, {
        [routeId]: {
          stages: {
            [stageId]: {
              $set: orderContents(contents),
            },
          },
        },
      });
    }

    case ContentsActionTypes.LOAD_STAGE_CONTENT: {
      const { routeId, stageId, contentId, content } = action.payload;
      const routeContents = state[routeId];
      if (!routeContents)
        return {
          ...state,
          [routeId]: {
            contents: [],
            stages: {
              [stageId]: [content],
            },
          },
        };

      const newContents = routeContents.stages[stageId].map((prevContent) =>
        prevContent.id === contentId ? { ...prevContent, ...content } : prevContent,
      );

      return update(state, {
        [routeId]: {
          stages: { [stageId]: { $set: orderContents(newContents) } },
        },
      });
    }

    case ContentsActionTypes.ADD_STAGE_CONTENT: {
      const { routeId, stageId, content } = action.payload;
      return update(state, {
        [routeId]: {
          stages: {
            [stageId]: {
              $push: [content],
            },
          },
        },
      });
    }

    case ContentsActionTypes.UPDATE_STAGE_CONTENT: {
      const { routeId, stageId, contentId, content } = action.payload;
      const routeContents = state[routeId];
      if (!routeContents) return state;
      const stageContents = routeContents.stages[stageId];
      if (!stageContents) return state;

      const newContents = stageContents.map((prevContent) =>
        prevContent.id === contentId ? { ...prevContent, ...content } : prevContent,
      );

      return update(state, {
        [routeId]: {
          stages: {
            [stageId]: {
              $set: orderContents(newContents),
            },
          },
        },
      });
    }

    case ContentsActionTypes.DELETE_STAGE_CONTENT: {
      const { routeId, stageId, contentId } = action.payload;
      const routeContents = state[routeId];
      if (!routeContents) return state;
      const stageContents = routeContents.stages[stageId];
      if (!stageContents) return state;

      const newContents = stageContents.filter((content) => content.id !== contentId);

      return update(state, {
        [routeId]: {
          stages: {
            [stageId]: {
              $set: orderContents(newContents),
            },
          },
        },
      });
    }

    default:
      return state;
  }
};
